/**
 * Reprojeção dos PNGs do radar para a projeção do mapa — funções puras,
 * testadas em `geometria.test.ts`.
 *
 * O PNG do SIPAM é uma grade AZIMUTAL EQUIDISTANTE (AEQD) centrada no radar
 * (ver `georef.ts`, com a validação). O mapa do AmaView está na grade fixa
 * do GOES-19, onde o quadrado do radar vira um quadrilátero curvo. Medido:
 * tratá-lo como retângulo erra até 27 pixels do radar (~13 km, São Luís); a
 * melhor transformação afim ainda erra 11. Então a imagem é de fato
 * reprojetada.
 *
 * Como: uma grade de saída alinhada ao mapa, com pixel quadrado do tamanho do
 * pixel do radar (não perde resolução), e para cada pixel de saída o pixel de
 * ORIGEM mais próximo. Vizinho mais próximo de propósito: o PNG é
 * classificado — cada cor é uma classe da legenda —, e interpolar criaria
 * cores que não existem na legenda. Nenhum valor é inventado ou alterado.
 *
 * Projetar ~1 milhão de pixels um a um custaria segundos; a inversa é
 * calculada só numa grade de controle (a cada `passo` px) e interpolada
 * bilinearmente entre os nós. As duas projeções são suaves nessa escala: o
 * erro da interpolação fica muito abaixo de um pixel (ver os testes).
 */
import type { Extent } from 'ol/extent';

/**
 * Onde cada ponto cai no PNG: (u, v) normalizados, 0–1 da borda esquerda à
 * direita e de cima para baixo (NaN fora do domínio da projeção).
 */
export interface ModeloOrigem {
  paraUV(lon: number, lat: number): [number, number];
  /** Inversa: (u, v) → lon/lat. */
  deUV(u: number, v: number): [number, number];
}

/** PNG como grade lon/lat simples (bordas externas = `extent`). Só para testes e reserva. */
export function modeloLonLat(extent: readonly [number, number, number, number]): ModeloOrigem {
  const [minx, miny, maxx, maxy] = extent;
  return {
    paraUV: (lon, lat) => [(lon - minx) / (maxx - minx), (maxy - lat) / (maxy - miny)],
    deUV: (u, v) => [minx + u * (maxx - minx), maxy - v * (maxy - miny)],
  };
}

export type Projetar = (lon: number, lat: number) => [number, number];

/** O que o worker precisa para montar a tabela de reprojeção (clonável). */
export interface GradeReprojecao {
  /**
   * Tamanho do PNG para o qual a grade foi calculada (o modelo AEQD depende
   * dele: o tamanho do pixel muda com o sistema do radar).
   */
  origem: [number, number];
  /** Tamanho (px) da imagem reprojetada. */
  largura: number;
  altura: number;
  /** Distância (px de saída) entre os nós da grade de controle. */
  passo: number;
  /** Nós da grade (incluem a última coluna/linha). */
  colunas: number;
  linhas: number;
  /** Posição (u, v) no PNG de origem, normalizada 0–1, em cada nó; NaN fora do disco. */
  uv: Float32Array;
}

export interface GeometriaRadar extends GradeReprojecao {
  /** Extensão da imagem reprojetada, em coordenadas do mapa. */
  extent: Extent;
}

/** Nós por borda ao projetar o contorno do quadrado (para achar o bbox curvo). */
const PONTOS_POR_BORDA = 64;

/**
 * Geometria da reprojeção de um radar: extensão no mapa, tamanho da saída e a
 * grade de controle.
 *
 * `px` = tamanho do PNG (define a resolução da saída — o PNG real pode ter
 * outro tamanho: a tabela é montada com o tamanho de verdade, porque (u, v) é
 * normalizado).
 */
export function calcularGeometria(
  modelo: ModeloOrigem,
  px: readonly [number, number],
  paraMapa: Projetar,
  paraLonLat: Projetar,
  passo = 16,
): GeometriaRadar {
  const [w, h] = px;

  // Contorno projetado → bbox no mapa.
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  const incluir = (u: number, v: number) => {
    const [lon, lat] = modelo.deUV(u, v);
    const [x, y] = paraMapa(lon, lat);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    x0 = Math.min(x0, x);
    y0 = Math.min(y0, y);
    x1 = Math.max(x1, x);
    y1 = Math.max(y1, y);
  };
  for (let k = 0; k <= PONTOS_POR_BORDA; k++) {
    const f = k / PONTOS_POR_BORDA;
    incluir(f, 0);
    incluir(f, 1);
    incluir(0, f);
    incluir(1, f);
  }
  if (!(x1 > x0 && y1 > y0)) throw new Error('Radar fora do disco visível do GOES-19');

  // Pixel de saída quadrado, do tamanho do MENOR lado do pixel do radar
  // projetado no centro: a saída nunca tem menos resolução que a origem.
  const noMapa = (u: number, v: number) => {
    const [lon, lat] = modelo.deUV(u, v);
    return paraMapa(lon, lat);
  };
  const [cx, cy] = noMapa(0.5, 0.5);
  const [ex, ey] = noMapa(0.5 + 1 / w, 0.5);
  const [sx, sy] = noMapa(0.5, 0.5 + 1 / h);
  const tamanho = Math.min(Math.hypot(ex - cx, ey - cy), Math.hypot(sx - cx, sy - cy));
  if (!(tamanho > 0)) throw new Error('Geometria do radar inválida');

  const largura = Math.ceil((x1 - x0) / tamanho);
  const altura = Math.ceil((y1 - y0) / tamanho);
  // Âncora no canto superior esquerdo; a saída cresce para a direita e para baixo.
  const extent: Extent = [x0, y1 - altura * tamanho, x0 + largura * tamanho, y1];

  // Nós SEMPRE a cada `passo` px — o último pode passar um pouco da borda.
  // Grampear o último nó na borda deixaria a última célula mais curta que as
  // outras, e a interpolação (que supõe espaçamento igual) erraria 2–3 px ali.
  const colunas = Math.ceil(largura / passo) + 1;
  const linhas = Math.ceil(altura / passo) + 1;
  const uv = new Float32Array(colunas * linhas * 2);
  for (let j = 0; j < linhas; j++) {
    const py = j * passo;
    for (let i = 0; i < colunas; i++) {
      const pxl = i * passo;
      const [lon, lat] = paraLonLat(x0 + pxl * tamanho, y1 - py * tamanho);
      const k = (j * colunas + i) * 2;
      const [u, v] = Number.isFinite(lon) && Number.isFinite(lat) ? modelo.paraUV(lon, lat) : [NaN, NaN];
      uv[k] = u;
      uv[k + 1] = v;
    }
  }
  return { extent, origem: [w, h], largura, altura, passo, colunas, linhas, uv };
}

/**
 * Tabela de reprojeção: para cada pixel de saída (linha a linha), o índice do
 * pixel de origem (`linha * larguraOrigem + coluna`), ou −1 fora do PNG.
 */
export function construirTabela(g: GradeReprojecao, larguraOrigem: number, alturaOrigem: number): Int32Array {
  const { largura, altura, passo, colunas, uv } = g;
  const tabela = new Int32Array(largura * altura);
  for (let oy = 0; oy < altura; oy++) {
    const fy = (oy + 0.5) / passo;
    const j = Math.min(Math.floor(fy), g.linhas - 2);
    const ty = fy - j;
    for (let ox = 0; ox < largura; ox++) {
      const fx = (ox + 0.5) / passo;
      const i = Math.min(Math.floor(fx), colunas - 2);
      const tx = fx - i;
      const a = (j * colunas + i) * 2;
      const b = a + 2;
      const c = a + colunas * 2;
      const d = c + 2;
      const u =
        (uv[a] * (1 - tx) + uv[b] * tx) * (1 - ty) + (uv[c] * (1 - tx) + uv[d] * tx) * ty;
      const v =
        (uv[a + 1] * (1 - tx) + uv[b + 1] * tx) * (1 - ty) + (uv[c + 1] * (1 - tx) + uv[d + 1] * tx) * ty;
      const col = Math.floor(u * larguraOrigem);
      const lin = Math.floor(v * alturaOrigem);
      // NaN (fora do disco) falha nas comparações e cai no −1.
      tabela[oy * largura + ox] =
        col >= 0 && col < larguraOrigem && lin >= 0 && lin < alturaOrigem ? lin * larguraOrigem + col : -1;
    }
  }
  return tabela;
}

/**
 * Aplica a tabela: copia o pixel (RGBA empacotado em 32 bits) de origem para
 * cada pixel de saída; fora do PNG, transparente. Não mistura cores.
 */
export function reprojetar(origem: Uint32Array, tabela: Int32Array, destino: Uint32Array): void {
  for (let k = 0; k < tabela.length; k++) {
    const s = tabela[k];
    destino[k] = s >= 0 ? origem[s] : 0;
  }
}

/** Pixel (coluna, linha) do PNG original que contém o ponto lon/lat, ou null fora dele. */
export function pixelDoPonto(
  modelo: ModeloOrigem,
  larguraOrigem: number,
  alturaOrigem: number,
  lon: number,
  lat: number,
): [number, number] | null {
  const [u, v] = modelo.paraUV(lon, lat);
  const col = Math.floor(u * larguraOrigem);
  const lin = Math.floor(v * alturaOrigem);
  return col >= 0 && col < larguraOrigem && lin >= 0 && lin < alturaOrigem ? [col, lin] : null;
}
